// components/employes/RadiationEmployeDialog.jsx
// ============================================================================
// RADIER UN EMPLOYÉ
// ============================================================================
// Remplace « Supprimer » dans la liste des employés. L'aperçu vient du
// serveur : ce qui sera coupé, ce qui reste dans l'historique, et ce qui
// empêche la radiation tant que ce n'est pas réglé (caisse ouverte, avance non
// soldée, dotation en attente, transfert en cours).
// ============================================================================

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, BadgeCheck, CalendarX, Info, KeyRound, Link2, Loader2, Lock, UserMinus, Users
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import ChampSelect from '../ui/ChampSelect';
import { useFeatures } from '../../contexts/FeaturesContext';
import { radiationEmployesApi } from '../../hooks/useRadiationEmployes';

const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const BOUTON_SECONDAIRE = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_RADIER = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md bg-[#c66d59] px-4 text-sm font-medium text-white transition hover:bg-[#ae5d4a] disabled:cursor-not-allowed disabled:opacity-50';

const formaterNombre = (n) => Number(n || 0).toLocaleString('fr-FR');
const formaterMontant = (montant) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`;

const LigneEffet = ({ icone: Icone, texte }) => (
  <li className="flex items-start gap-2 text-sm text-gray-700">
    <Icone size={15} className="mt-0.5 shrink-0 text-gray-400" />
    <span>{texte}</span>
  </li>
);

const RadiationEmployeDialog = ({ employe, open, onClose, onRadie }) => {
  const { estActive } = useFeatures();
  const parValidation = estActive('module.comptabilite');

  const [motifs, setMotifs] = useState([]);
  const [apercu, setApercu] = useState(null);
  // Vrai dès le premier rendu : sinon la fenêtre s'ouvrait vide, puis passait
  // à la roue, puis au contenu (clignotement).
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [form, setForm] = useState({ motif_id: '', date_sortie: '', commentaire: '', liberer_badge: false });

  // Ouverture : motifs et premier aperçu (date de sortie = aujourd'hui).
  useEffect(() => {
    if (!open || !employe?.id) return undefined;
    let annule = false;
    setErreur('');
    setApercu(null);
    setForm({ motif_id: '', date_sortie: '', commentaire: '', liberer_badge: false });
    setChargement(true);
    Promise.all([radiationEmployesApi.motifs(), radiationEmployesApi.apercu(employe.id)])
      .then(([listeMotifs, premierApercu]) => {
        if (annule) return;
        setMotifs(listeMotifs);
        setApercu(premierApercu);
        setForm((prev) => ({ ...prev, date_sortie: premierApercu.date_sortie }));
      })
      .catch((error) => { if (!annule) setErreur(error.message); })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [open, employe?.id]);

  // Le nombre de créneaux à réaffecter dépend de la date de sortie.
  const changerDate = async (dateSortie) => {
    setForm((prev) => ({ ...prev, date_sortie: dateSortie }));
    if (!dateSortie || !employe?.id) return;
    try {
      setApercu(await radiationEmployesApi.apercu(employe.id, { date_sortie: dateSortie }));
    } catch (error) {
      setErreur(error.message);
    }
  };

  const blocages = apercu?.blocages || [];
  const peutEnvoyer = Boolean(apercu) && blocages.length === 0 && Boolean(form.motif_id) && Boolean(form.date_sortie) && !envoi;

  const envoyer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    setErreur('');
    try {
      const resultat = await radiationEmployesApi.radier(employe.id, {
        motif_id: Number(form.motif_id),
        date_sortie: form.date_sortie,
        commentaire: form.commentaire.trim() || null,
        liberer_badge: form.liberer_badge
      });
      onRadie?.(resultat);
    } catch (error) {
      setErreur(error.message);
    } finally {
      setEnvoi(false);
    }
  };

  const coupe = apercu?.coupe || {};
  const conserve = apercu?.conserve || {};

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-4xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#c66d59] text-white">
            <UserMinus size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Radier {employe ? `${employe.prenom || ''} ${employe.nom || ''}`.trim() : "l'employé"}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Son accès et son badge sont coupés ; tout son historique est conservé.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-5 py-4">
          {chargement ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="animate-spin text-[#246990]" size={28} />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-4">
                {erreur && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</div>
                )}

                {blocages.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
                      <Lock size={16} />
                      Radiation impossible pour l'instant
                    </div>
                    <div className="space-y-3">
                      {blocages.map((blocage) => (
                        <div key={blocage.code}>
                          <p className="text-sm text-red-700">{blocage.message}</p>
                          {/* Les lignes à régler : ce que la RH transmet à la
                              comptabilité, sans avoir à les rechercher. */}
                          {blocage.details?.length > 0 && (
                            <div className="mt-2 overflow-x-auto rounded-md border border-red-200 bg-white">
                              <table className="w-full text-sm">
                                <tbody className="divide-y divide-red-100">
                                  {blocage.details.map((ligne) => (
                                    <tr key={ligne.id}>
                                      <td className="px-3 py-2">
                                        <p className="font-medium text-gray-900">{ligne.libelle}</p>
                                        {ligne.precision && <p className="text-xs text-gray-500">{ligne.precision}</p>}
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">{ligne.statut}</td>
                                      {/* Les actifs détenus (Logistique) n'ont pas de montant à régler. */}
                                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                                        {ligne.montant !== undefined && ligne.montant !== null ? formaterMontant(ligne.montant) : ''}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {blocage.details.length > 1 && blocage.total !== undefined && (
                                  <tfoot>
                                    <tr className="border-t border-red-200 bg-red-50/60">
                                      <td className="px-3 py-2 text-xs font-semibold text-red-800" colSpan={2}>Total à régler</td>
                                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-red-800">
                                        {formaterMontant(blocage.total)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Motif de sortie</span>
                      <ChampSelect
                        valeur={form.motif_id || undefined}
                        onChange={(valeur) => setForm((prev) => ({ ...prev, motif_id: valeur }))}
                        options={motifs.map((motif) => ({ valeur: motif.id, libelle: motif.libelle }))}
                        placeholder="Choisir un motif"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Date de sortie</span>
                      <input
                        type="date"
                        value={form.date_sortie}
                        max={apercu?.date_du_jour || undefined}
                        onChange={(event) => changerDate(event.target.value)}
                        className={CHAMP}
                      />
                    </label>
                  </div>

                  <label className="mt-4 block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Commentaire (facultatif)</span>
                    <textarea
                      rows={3}
                      value={form.commentaire}
                      onChange={(event) => setForm((prev) => ({ ...prev, commentaire: event.target.value }))}
                      className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                    />
                  </label>

                  {coupe.badge && (
                    <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <input
                        type="checkbox"
                        checked={form.liberer_badge}
                        onChange={(event) => setForm((prev) => ({ ...prev, liberer_badge: event.target.checked }))}
                        className="mt-0.5 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-800">Libérer le badge{coupe.numero_badge ? ` ${coupe.numero_badge}` : ''}</span>
                        <span className="block text-xs text-gray-500">Il pourra être redonné à un autre employé. Sinon, il reste attaché à cette fiche et refusé à la pointeuse.</span>
                      </span>
                    </label>
                  )}
                </div>

                {apercu?.avertissements?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <ul className="space-y-1.5">
                      {apercu.avertissements.map((texte) => (
                        <li key={texte} className="flex items-start gap-2 text-sm text-amber-800">
                          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                          <span>{texte}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <aside className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="mb-3 text-sm font-semibold text-gray-900">Ce qui est coupé</p>
                  <ul className="space-y-2">
                    <LigneEffet icone={KeyRound} texte={coupe.acces_application ? "Accès à l'application fermé, sessions déconnectées" : "Pas d'accès à l'application"} />
                    <LigneEffet icone={BadgeCheck} texte={coupe.badge ? 'Badge refusé à la pointeuse' : 'Pas de badge'} />
                    <LigneEffet icone={Users} texte={coupe.enfants_a_charge > 0
                      ? `${coupe.enfants_a_charge} liaison(s) enfant à charge terminée(s), retenues arrêtées`
                      : 'Aucun enfant à charge'} />
                    {coupe.liaisons_inter_sites > 0 && (
                      <LigneEffet icone={Link2} texte={`${coupe.liaisons_inter_sites} liaison(s) inter-sites suspendue(s)`} />
                    )}
                    <LigneEffet icone={CalendarX} texte={coupe.creneaux_a_reaffecter > 0
                      ? `${formaterNombre(coupe.creneaux_a_reaffecter)} créneau(x) après la sortie à réaffecter`
                      : "Aucun créneau d'emploi du temps après la sortie"} />
                  </ul>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="mb-3 text-sm font-semibold text-gray-900">Ce qui est conservé</p>
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    <li>{formaterNombre(conserve.paiements_salaire)} paiement(s) de salaire</li>
                    <li>{formaterNombre(conserve.avances)} avance(s) sur salaire</li>
                    <li>{formaterNombre(conserve.transactions_caisse)} transaction(s) de caisse</li>
                    <li>{formaterNombre(conserve.creneaux_passes)} cours donné(s)</li>
                    <li>{formaterNombre(conserve.pointages)} pointage(s)</li>
                  </ul>
                </div>

                {parValidation && (
                  <div className="flex items-start gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-[#246990]">
                    <Info size={15} className="mt-0.5 shrink-0" />
                    <span>La radiation sera exécutée après validation au Centre de Support.</span>
                  </div>
                )}
              </aside>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
          <button type="button" onClick={onClose} disabled={envoi} className={BOUTON_SECONDAIRE}>
            Annuler
          </button>
          <button type="button" onClick={envoyer} disabled={!peutEnvoyer} className={BOUTON_RADIER}>
            {envoi ? <Loader2 className="animate-spin" size={16} /> : <UserMinus size={16} />}
            {parValidation ? 'Demander la radiation' : 'Radier'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RadiationEmployeDialog;
